import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { BOQSummary } from "./boqCalculationService";

export type PdfOutputDisposition = "download" | "blob" | "both";

export interface GeneratePdfOptions {
  disposition?: PdfOutputDisposition;
}

interface ProjectData {
  projectName: string;
  locationName: string;
  latitude: number;
  longitude: number;
  altitude: number;
  tilt: number;
  azimuth: number;
  mountingType: string;
  surfaceType: string;
  monthlyConsumption: number[];
  systemPower: number;
  annualProduction: number;
  specificProduction: number;
  performanceRatio: number;
  boq?: BOQSummary;
  monthlyResults: Array<{
    month: number;
    globHor: number;
    diffHor: number;
    tempAmb: number;
    globInc: number;
    globEff: number;
    eArray: number;
    eGrid: number;
    pr: number; // Performance Ratio in percent (0–100)
  }>;
  systemConfig: {
    modulesPerString: number;
    stringsParallel: number;
    nInverters: number;
    modulePower: number;
    inverterPower: number;
    totalModules: number;
    dcAcRatio?: number;
  };
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export async function generatePDFReport(
  data: ProjectData,
  options?: GeneratePdfOptions
): Promise<void | Blob> {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const formatSar = (amount: number) =>
    new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const formatKw = (value: number) =>
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value);

  let yPos = 20;

  // Header
  doc.setFontSize(24);
  doc.setTextColor(234, 88, 12); // Orange color
  doc.text("☀", 15, yPos);
  doc.setTextColor(0, 0, 0);
  doc.text("Solar PV System Design Report", 25, yPos);

  yPos += 15;
  doc.setFontSize(11);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 15, yPos);

  // Project Summary Box
  yPos += 10;
  doc.setFillColor(250, 250, 250);
  doc.rect(15, yPos, 180, 40, "F");
  
  yPos += 8;
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text(data.projectName, 20, yPos);

  yPos += 8;
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text(`Location: ${data.locationName}`, 20, yPos);
  doc.text(`Coordinates: ${data.latitude.toFixed(4)}°, ${data.longitude.toFixed(4)}°`, 110, yPos);

  yPos += 6;
  doc.text(`Altitude: ${data.altitude} m`, 20, yPos);
  doc.text(`Tilt: ${data.tilt}° | Azimuth: ${data.azimuth}°`, 110, yPos);

  yPos += 6;
  doc.text(`Mounting: ${data.mountingType} | Surface: ${data.surfaceType}`, 20, yPos);

  // Key Results
  yPos += 15;
  doc.setFontSize(14);
  doc.setTextColor(234, 88, 12);
  doc.text("Key Results", 15, yPos);

  yPos += 5;
  const resultsData = [
    ["System Size", `${data.systemPower.toFixed(2)} kWp`],
    ["Annual Production", `${data.annualProduction.toFixed(0)} kWh/year`],
    ["Specific Production", `${data.specificProduction.toFixed(0)} kWh/kWp/year`],
    ["Performance Ratio", `${data.performanceRatio.toFixed(1)}%`],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [["Parameter", "Value"]],
    body: resultsData,
    theme: "grid",
    headStyles: { fillColor: [234, 88, 12], textColor: 255 },
    margin: { left: 15, right: 15 },
    styles: { fontSize: 10 },
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // System Configuration
  doc.setFontSize(14);
  doc.setTextColor(234, 88, 12);
  doc.text("System Configuration", 15, yPos);

  yPos += 5;
  const configData = [
    ["Total Modules", data.systemConfig.totalModules.toString()],
    ["Module Power", `${data.systemConfig.modulePower} Wp`],
    ["Modules per String", data.systemConfig.modulesPerString.toString()],
    ["Strings in Parallel", data.systemConfig.stringsParallel.toString()],
    ["Number of Inverters", data.systemConfig.nInverters.toString()],
    ["Inverter Power", `${formatKw(data.systemConfig.inverterPower)} kW`],
    ["DC/AC Ratio", typeof data.systemConfig.dcAcRatio === "number" ? data.systemConfig.dcAcRatio.toFixed(3) : "N/A"],
  ];

  autoTable(doc, {
    startY: yPos,
    body: configData,
    theme: "plain",
    margin: { left: 15, right: 15 },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 60 },
      1: { cellWidth: 120 },
    },
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Add new page for monthly results
  doc.addPage();
  yPos = 20;

  doc.setFontSize(14);
  doc.setTextColor(234, 88, 12);
  doc.text("Monthly Energy Balance", 15, yPos);

  yPos += 5;
  const monthlyData = data.monthlyResults.map((m) => [
    MONTH_NAMES[m.month - 1],
    m.globHor.toFixed(1),
    m.tempAmb.toFixed(1),
    m.globInc.toFixed(1),
    m.eArray.toFixed(0),
    m.eGrid.toFixed(0),
    m.pr.toFixed(1),
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [["Month", "GHI\n(kWh/m²)", "Temp\n(°C)", "POA\n(kWh/m²)", "Array\n(kWh)", "Grid\n(kWh)", "PR\n(%)"]],
    body: monthlyData,
    theme: "striped",
    headStyles: { fillColor: [234, 88, 12], textColor: 255, halign: "center" },
    margin: { left: 15, right: 15 },
    styles: { fontSize: 9, halign: "right" },
    columnStyles: {
      0: { halign: "left", fontStyle: "bold" },
    },
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Annual totals
  const totalGHI = data.monthlyResults.reduce((sum, m) => sum + m.globHor, 0);
  const totalArray = data.monthlyResults.reduce((sum, m) => sum + m.eArray, 0);
  const totalGrid = data.monthlyResults.reduce((sum, m) => sum + m.eGrid, 0);
  const avgPR = data.monthlyResults.reduce((sum, m) => sum + m.pr, 0) / 12;

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`Annual GHI: ${totalGHI.toFixed(0)} kWh/m²`, 15, yPos);
  doc.text(`Array Output: ${totalArray.toFixed(0)} kWh`, 15, yPos + 6);
  doc.text(`Grid Output: ${totalGrid.toFixed(0)} kWh`, 15, yPos + 12);
  doc.text(`Average PR: ${avgPR.toFixed(1)}%`, 15, yPos + 18);

  // Loss Diagram (text-based)
  yPos += 30;
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(234, 88, 12);
  doc.text("Energy distribution diagram", 15, yPos);

  yPos += 10;
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);

  // Calculate losses
  const solarIrradiation = totalGHI * data.systemPower;
  const arrayLosses = solarIrradiation - totalArray;
  const inverterLosses = totalArray - totalGrid;
  const totalLosses = solarIrradiation - totalGrid;

  const lossData = [
    ["Solar Irradiation on Array", `${solarIrradiation.toFixed(0)} kWh`, "100%"],
    ["  - Array Losses (temp, mismatch, wiring)", `-${arrayLosses.toFixed(0)} kWh`, `-${((arrayLosses / solarIrradiation) * 100).toFixed(1)}%`],
    ["= Array Output (DC)", `${totalArray.toFixed(0)} kWh`, `${((totalArray / solarIrradiation) * 100).toFixed(1)}%`],
    ["  - Inverter Losses", `-${inverterLosses.toFixed(0)} kWh`, `-${((inverterLosses / solarIrradiation) * 100).toFixed(1)}%`],
    ["= Energy to Grid (AC)", `${totalGrid.toFixed(0)} kWh`, `${((totalGrid / solarIrradiation) * 100).toFixed(1)}%`],
    ["", "", ""],
    ["Total System Losses", `-${totalLosses.toFixed(0)} kWh`, `-${((totalLosses / solarIrradiation) * 100).toFixed(1)}%`],
  ];

  autoTable(doc, {
    startY: yPos,
    body: lossData,
    theme: "plain",
    margin: { left: 20, right: 15 },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 90 },
      1: { cellWidth: 50, halign: "right" },
      2: { cellWidth: 30, halign: "right" },
    },
    didParseCell: (data: any) => {
      if (data.row.index === 2 || data.row.index === 4) {
        data.cell.styles.fontStyle = "bold";
      }
      if (data.row.index === 6) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [255, 240, 230];
      }
    },
  });

  // BOQ section (optional)
  if (data.boq) {
    doc.addPage();
    yPos = 20;
    const pricePerKwp =
      Number.isFinite(data.systemPower) && data.systemPower > 0
        ? data.boq.grandTotal / data.systemPower
        : null;

    doc.setFontSize(14);
    doc.setTextColor(234, 88, 12);
    doc.text("Bill of Quantities (BOQ)", 15, yPos);

    yPos += 8;
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(
      `Subtotal: ${formatSar(data.boq.subtotal)} | VAT (15%): ${formatSar(data.boq.vat)} | Total: ${formatSar(data.boq.grandTotal)}${
        pricePerKwp != null ? ` | Price per kWp: ${formatSar(pricePerKwp)}/kWp` : ''
      }`,
      15,
      yPos
    );

    yPos += 6;

    const boqRows = data.boq.items.map((item) => ([
      item.category,
      item.description,
      item.quantity.toLocaleString('en-US', { maximumFractionDigits: 0 }),
      item.unit,
      formatSar(item.unitPrice),
      formatSar(item.totalPrice),
    ]));

    autoTable(doc, {
      startY: yPos,
      head: [["Category", "Item", "Qty", "Unit", "Unit Price", "Total"]],
      body: boqRows,
      theme: "striped",
      headStyles: { fillColor: [234, 88, 12], textColor: 255 },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 62 },
        2: { halign: "right", cellWidth: 14 },
        3: { cellWidth: 14 },
        4: { halign: "right", cellWidth: 30 },
        5: { halign: "right", cellWidth: 30 },
      },
      margin: { left: 15, right: 15 },
    });
  }

  // Disclaimer on last page
  doc.addPage();
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text("Disclaimer", 20, 30);
  
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  const disclaimerText = [
    "This solar PV system design report is generated using industry-standard PVSyst-inspired",
    "calculation methodology. The results represent preliminary estimates suitable for initial",
    "feasibility assessment and project planning purposes.",
    "",
    "Expected Accuracy: ±5-7% for standard configurations in typical Saudi Arabian conditions.",
    "",
    "The calculations account for:",
    "  • Solar irradiation transposition using Perez model",
    "  • Incidence Angle Modifier (IAM) based on Fresnel reflection",
    "  • Temperature effects using NOCT-based cell temperature model",
    "  • System losses (soiling, mismatch, wiring, inverter efficiency)",
    "  • Performance ratio and capacity factor calculations",
    "",
    "Recommendations:",
    "  • For systems larger than 100 kWp: Professional validation recommended",
    "  • For complex shading scenarios: Detailed 3D shading analysis recommended",
    "  • For critical projects: Cross-validation with PVSyst or similar tools recommended",
    "",
    "Actual system performance may vary based on:",
    "  • Installation quality and workmanship",
    "  • Module and inverter selection and quality",
    "  • Maintenance schedule and cleaning frequency",
    "  • Shading conditions and site-specific factors",
    "  • Weather variations from historical averages",
    "",
    "This report should not be used as the sole basis for financial investment decisions.",
    "Professional engineering review and site assessment are recommended before installation."
  ];
  
  // Start disclaimer body below the section title on the new page
  let disclaimerYPos = 40;
  disclaimerText.forEach(line => {
    doc.text(line, 20, disclaimerYPos);
    disclaimerYPos += 5;
  });
  
  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Solar PV System Design Tool - Page ${i} of ${pageCount} | Accuracy: ±5-7%`,
      105,
      287,
      { align: "center" }
    );
  }

  const filename = `${data.projectName.replace(/[^a-z0-9]/gi, "_")}_Solar_Report.pdf`;
  const disposition = options?.disposition ?? "download";
  if (disposition === "blob") {
    return doc.output("blob");
  }
  if (disposition === "both") {
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return blob;
  }
  doc.save(filename);
}
